import type { MindmapFarbe, MindmapSymbol } from '@beholder/shared/module/mindmap';

/** Farbtöne der Knoten – kräftig genug für hellen und dunklen Hintergrund (Fläche wird halbdurchsichtig gefüllt). */
export const FARBTON: Record<MindmapFarbe, string> = {
  blau: '#3b6fd8',
  gruen: '#2f9e5b',
  gelb: '#c99a12',
  orange: '#e07b28',
  rot: '#d64545',
  lila: '#8a55d0',
  tuerkis: '#1f9ea6',
  grau: '#7a8291',
};

export const FARB_NAMEN: Record<MindmapFarbe, string> = {
  blau: 'Blau',
  gruen: 'Grün',
  gelb: 'Gelb',
  orange: 'Orange',
  rot: 'Rot',
  lila: 'Lila',
  tuerkis: 'Türkis',
  grau: 'Grau',
};

/** Reihenfolge der automatischen Astfarben (erste Ebene). */
export const AST_FARBEN: MindmapFarbe[] = ['blau', 'orange', 'gruen', 'lila', 'rot', 'tuerkis', 'gelb', 'grau'];

/** Pfade der Knotensymbole (24×24, Strich). */
export const SYMBOL_PFAD: Record<MindmapSymbol, string> = {
  stern: 'M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8-4.3-4.1 5.9-.8z',
  haken: 'M4.5 12.5l4.5 4.5 10.5-10.5',
  warnung: 'M12 3.5l9 16H3zM12 10v4.5M12 17.2v.3',
  frage: 'M9 9.2a3 3 0 1 1 4.4 2.6c-.9.5-1.4 1.1-1.4 2.2M12 17.3v.3M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
  idee: 'M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z',
  flagge: 'M5 21V4M5 4h12l-2.5 4L17 12H5',
  herz: 'M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.4 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z',
  uhr: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2',
  geld: 'M17 6.5A6.5 6.5 0 1 0 17 17.5M4.5 10.5h8M4.5 13.5h8',
  person: 'M12 11a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6zM4.5 20.5c.9-4 3.9-6 7.5-6s6.6 2 7.5 6',
  ort: 'M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11zM12 12.3a2.3 2.3 0 1 0 0-4.6 2.3 2.3 0 0 0 0 4.6z',
  blitz: 'M13 2.5L5 13.5h6l-1 8 8-11h-6z',
};

export const SYMBOL_NAMEN: Record<MindmapSymbol, string> = {
  stern: 'Stern',
  haken: 'Erledigt',
  warnung: 'Achtung',
  frage: 'Frage',
  idee: 'Idee',
  flagge: 'Ziel',
  herz: 'Wichtig',
  uhr: 'Termin',
  geld: 'Geld',
  person: 'Person',
  ort: 'Ort',
  blitz: 'Dringend',
};

/** Büroklammer (Anlagen) und Notizzettel als Pfade. */
export const KLAMMER_PFAD = 'M16.5 7.5l-7 7a2.1 2.1 0 0 0 3 3l7.5-7.5a4 4 0 0 0-5.7-5.7L6.8 11.8a5.9 5.9 0 0 0 8.4 8.4l6-6';
export const NOTIZ_PFAD = 'M5 4h14v11l-5 5H5zM14 20v-5h5M8.5 9h7M8.5 12.5h4';

export function SymbolBild({ symbol, groesse = 18 }: { symbol: MindmapSymbol; groesse?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={groesse} height={groesse} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={SYMBOL_PFAD[symbol]} />
    </svg>
  );
}

export function PfadBild({ d, groesse = 16 }: { d: string; groesse?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={groesse} height={groesse} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

/** Feste Farbe je Person (für Anwesenheit). */
export function personFarbe(benutzerId: string): string {
  let h = 0;
  for (const c of benutzerId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const t = Object.values(FARBTON);
  return t[h % t.length]!;
}

export function kuerzel(name: string): string {
  const teile = name.trim().split(/\s+/);
  return ((teile[0]?.[0] ?? '?') + (teile.length > 1 ? (teile[teile.length - 1]?.[0] ?? '') : '')).toUpperCase();
}
